import { formatWorkstream } from '/packages/shared-components/utils/util';

/** Markdown link/image: `[text](url)` or `![alt](url)`; url may contain one balanced `(…)` pair. */
export const MARKDOWN_LINK_PATTERN = /!?\[[^\]]*\]\((?:[^()]|\([^()]*\))*\)/g;

const WORKSTREAM_ENUM_PATTERN =
  /\b(?:ADK_Integration|Android_TV|Billing_API|Browser_Based_Device|Content_Provider|Content_Metadata|Customer_Journey|General|Perks)\b/g;

const MD_LINK_PLACEHOLDER_PREFIX = '\uE000MDL';
const MD_LINK_PLACEHOLDER_SUFFIX = '\uE001';

/** Do not match URLs already in markdown `(url)` or inside a `[` label. */
export const BARE_URL_IN_TEXT = /(?<!\]\()(?<!\[)https?:\/\/[^\s\]`<>]+/g;

function parenBalance(s: string): { open: number; close: number } {
  let open = 0;
  let close = 0;
  for (const ch of s) {
    if (ch === '(' || ch === '（') open += 1;
    else if (ch === ')' || ch === '）') close += 1;
  }
  return { open, close };
}

/** Drop extra closing parens (ASCII or fullwidth) when they exceed opens — e.g. `(https://x.com)` or `((url))`. */
function stripExtraClosingParens(s: string): string {
  let out = s;
  for (;;) {
    const last = out[out.length - 1];
    if (last !== ')' && last !== '）') break;
    const { open, close } = parenBalance(out);
    if (close > open) {
      out = out.slice(0, -1);
    } else {
      break;
    }
  }
  return out;
}

/**
 * JS URL() accepts `https://example.com)` as a path ending in `)`, which looks like markdown `)`.
 * If the string has no `(` / `（`, any trailing `)` / `）` cannot be part of a balanced path pair — strip them.
 */
function stripTrailingCloseWhenNoOpenParen(s: string): string {
  let out = s;
  while (out.length > 0) {
    const last = out[out.length - 1];
    if (last !== ')' && last !== '）') break;
    if (/[(（]/.test(out)) break;
    out = out.slice(0, -1);
  }
  return out;
}

function isValidHttpUrl(candidate: string): boolean {
  try {
    const u = new URL(candidate);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Non-`)` chars often glued after URLs; `)` / `）` are handled in peelTrailingJunkFromValidUrl with a paren-balance check
 * so paths like `wiki/Foo_(bar)` stay intact.
 */
function isUrlTrailingJunkChar(ch: string): boolean {
  switch (ch) {
    case ']':
    case '］':
    case '}':
    case '.':
    case ',':
    case ';':
    case ':':
    case '!':
    case '?':
    case '"':
    case "'":
    case '\u2019': // '
    case '\u201d': // "
    case '`':
    case '\u02CB':
    case '\uFF40':
    case '´':
    case '\u2018':
    case '》':
    case '」':
    case '』':
      return true;
    default:
      return false;
  }
}

function peelLeadingAsterisks(candidate: string): string {
  return candidate.replace(/^\*+/, '');
}

function peelTrailingJunkFromValidUrl(candidate: string): string {
  let out = candidate;
  while (out.length > 1) {
    const ch = out[out.length - 1];
    const shorter = out.slice(0, -1);

    if (ch === ')' || ch === '）') {
      if (!isValidHttpUrl(shorter)) break;
      const { open, close } = parenBalance(shorter);
      if (open !== close) break;
      out = shorter;
      continue;
    }

    // Markdown bold/italic: `**https://x.com**` — `\S+` captures the closing `**`; URL() treats it as path.
    // Do not strip a lone `*` when it is immediately after `/` (e.g. `.../*` wildcard paths).
    if (ch === '*') {
      if (out.length >= 2 && out[out.length - 2] === '/') break;
      if (!isValidHttpUrl(shorter)) break;
      out = shorter;
      continue;
    }

    if (!isUrlTrailingJunkChar(ch)) break;
    if (!isValidHttpUrl(shorter)) break;
    out = shorter;
  }
  return out;
}

/** Peel trailing characters until `raw` is a valid http(s) URL (e.g. strips trailing `).` from markdown). */
export function normalizeHttpUrl(raw: string): { href: string; display: string } | null {
  let candidate = peelLeadingAsterisks(stripExtraClosingParens(raw));
  candidate = stripTrailingCloseWhenNoOpenParen(candidate);
  while (candidate.length > 0) {
    if (isValidHttpUrl(candidate)) {
      const cleaned = peelTrailingJunkFromValidUrl(candidate);
      if (!isValidHttpUrl(cleaned)) {
        candidate = candidate.slice(0, -1);
        continue;
      }
      const u = new URL(cleaned);
      return { href: u.href, display: cleaned };
    }
    candidate = candidate.slice(0, -1);
  }
  return null;
}

/**
 * Markdown link syntax with optional inline-code backticks in the label and/or URL.
 * LLMs sometimes emit `` [`text`](url`) `` or `` `[text](url)` ``, which breaks link parsing.
 */
const MARKDOWN_LINK_WITH_CODE_PATTERN = /!?\[(?:[^`\]\n]|`[^`\n]*`)*?\]\((?:[^()\n]|`[^`\n]*`|\([^()]*\))*\)/g;

/** Strip inline-code backticks from markdown links so `[text](url)` always renders as a link. */
export function stripCodeFromMarkdownLinks(text: string): string {
  const withoutBackticks = (link: string) => link.replace(/`/g, '');

  let out = text.replace(new RegExp(`\`+(${MARKDOWN_LINK_WITH_CODE_PATTERN.source})\`+`, 'g'), (_, link: string) =>
    withoutBackticks(link),
  );

  out = out.replace(MARKDOWN_LINK_WITH_CODE_PATTERN, (match) =>
    match.includes('`') ? withoutBackticks(match) : match,
  );

  return out;
}

const MARKDOWN_HTTP_LINK_INNER = /\[[^\]]+\]\(\s*https?:\/\//;
const LABEL_ONLY_HTTP_LINK = /^\[https?:\/\/[^\]]+\]$/;
const INLINE_CODE_FENCE = '[`´\\u2018\\u2019]';

/** LLM wraps only the label in backticks: `` `[text]`(https://url`) `` — splits the link in two. */
export function mergeSplitMarkdownLinks(text: string): string {
  const splitLink = new RegExp(
    `${INLINE_CODE_FENCE}(\\[[^\\u0060\\u00B4\\u2018\\u2019\\u02CB\\uFF40\\n]+\\])${INLINE_CODE_FENCE}\\s*\\(([^)\\n]+)\\)`,
    'g',
  );
  return text.replace(splitLink, (_match, label: string, rawUrl: string) => {
    const norm = normalizeHttpUrl(rawUrl.replace(/[`´'\u2018\u2019\u02CB\uFF40]/g, ''));
    if (!norm) return `${label}(${rawUrl})`;
    return `${label}(${norm.href})`;
  });
}

/** Remove stray backticks/quotes immediately before the closing `)` of an http(s) link destination. */
export function stripStrayBackticksFromLinkDestinations(text: string): string {
  return text
    .replace(/(\]\(\s*https?:\/\/[^)]*?)[`´'\u2018\u2019\u02CB\uFF40](\))/g, '$1$2')
    .replace(/(\(\s*https?:\/\/[^)]*?)[`´'\u2018\u2019\u02CB\uFF40](\))/g, '$1$2');
}

/** While streaming, drop a trailing backtick that would break an in-progress http(s) link. */
export function stripIncompleteTrailingLinkBackticks(text: string): string {
  return text
    .replace(/(\]\(\s*https?:\/\/[^\s)]*)[`´'\u2018\u2019\u02CB\uFF40](?=$)/g, '$1')
    .replace(/(\(\s*https?:\/\/[^\s)]*)[`´'\u2018\u2019\u02CB\uFF40](?=$)/g, '$1');
}

/**
 * LLMs often wrap examples in inline code, e.g. `` `[url](url`)` ``.
 * The stray `` ` `` before `)` closes the code span early and leaves literal markdown on screen.
 */
export function unwrapInlineCodeContainingLinks(text: string): string {
  const inlineCode = new RegExp(
    `${INLINE_CODE_FENCE}([^\\u0060\\u00B4\\u2018\\u2019\\u02CB\\uFF40\\n]+)${INLINE_CODE_FENCE}`,
    'g',
  );
  let out = text.replace(inlineCode, (full, inner: string) => {
    const trimmed = inner.trim();
    if (MARKDOWN_HTTP_LINK_INNER.test(inner) || LABEL_ONLY_HTTP_LINK.test(trimmed)) {
      return inner;
    }
    return full;
  });
  out = stripStrayBackticksFromLinkDestinations(out);
  out = out.replace(/[`´'\u2018\u2019\u02CB\uFF40]\s*$/g, '');
  return out;
}

/** Merge plain split links: `[https://url]` + `(https://url`)` without backticks between them. */
export function mergeAdjacentBracketLabelAndParenUrl(text: string): string {
  return text.replace(
    /(\[https?:\/\/[^\]]+\])\s*\(\s*(https?:\/\/[^)\s`]+[^)]*?)\)/g,
    (_match, label: string, rawUrl: string) => {
      const norm = normalizeHttpUrl(rawUrl.replace(/[`´'\u2018\u2019\u02CB\uFF40]/g, ''));
      if (!norm) return `${label}(${rawUrl})`;
      return `${label}(${norm.href})`;
    },
  );
}

/** Run all link-repair passes; safe to call multiple times and on partial stream text. */
export function repairAssistantLinkMarkdown(text: string): string {
  let out = mergeSplitMarkdownLinks(text);
  out = unwrapInlineCodeContainingLinks(out);
  out = mergeAdjacentBracketLabelAndParenUrl(out);
  out = stripCodeFromMarkdownLinks(out);
  out = normalizeMarkdownLinkUrls(out);
  out = stripStrayBackticksFromLinkDestinations(out);
  out = stripIncompleteTrailingLinkBackticks(out);
  return out;
}

/** Normalize `[text](url)` hrefs — LLMs often append `'`, `` ` ``, or `)` inside the URL parens. */
export function normalizeMarkdownLinkUrls(text: string): string {
  return text.replace(MARKDOWN_LINK_PATTERN, (match) => {
    const bang = match.startsWith('!') ? '!' : '';
    const labelEnd = match.indexOf('](');
    if (labelEnd === -1) return match;

    const label = match.slice(bang.length + 1, labelEnd);
    const rawUrl = match.slice(labelEnd + 2, -1).replace(/`/g, '');
    const norm = normalizeHttpUrl(rawUrl);
    if (!norm) return match;

    return `${bang}[${label}](${norm.href})`;
  });
}

const MARKDOWN_LINK_QUOTE = /['\u2018\u2019]/;

/** Drop a single quote immediately before/after a markdown link when it is clearly punctuation. */
export function stripAdjacentQuotesFromMarkdownLinks(text: string): string {
  return text
    .replace(new RegExp(`${MARKDOWN_LINK_QUOTE.source}(!?\\[[^\\]]+\\]\\([^)]+\\))`, 'g'), '$1')
    .replace(new RegExp(`(!?\\[[^\\]]+\\]\\([^)]+\\))${MARKDOWN_LINK_QUOTE.source}(?=\\s|$|[.,;:!?])`, 'g'), '$1');
}

/** LLMs often wrap links or bare URLs in `**`; unwrap so markdown renders cleanly. */
export function stripBoldWrappingAroundMarkdown(text: string): string {
  let out = text.replace(new RegExp(`\\*\\*(${MARKDOWN_LINK_PATTERN.source})\\*\\*`, 'g'), '$1');
  out = out.replace(/\*\*(https?:\/\/\S+?)\*\*/g, '$1');
  return out;
}

export function protectMarkdownLinks(text: string): { protectedText: string; links: string[] } {
  const links: string[] = [];
  const protectedText = text.replace(MARKDOWN_LINK_PATTERN, (match) => {
    const idx = links.length;
    links.push(match);
    return `${MD_LINK_PLACEHOLDER_PREFIX}${idx}${MD_LINK_PLACEHOLDER_SUFFIX}`;
  });
  return { protectedText, links };
}

export function restoreMarkdownLinks(text: string, links: string[]): string {
  const re = new RegExp(`${MD_LINK_PLACEHOLDER_PREFIX}(\\d+)${MD_LINK_PLACEHOLDER_SUFFIX}`, 'g');
  return text.replace(re, (_, idx) => links[Number(idx)] ?? '');
}

/** Wrap bare http(s) URLs as markdown links so ReactMarkdown can render them (reuses URL normalization). */
export function linkifyBareUrlsForMarkdown(text: string): string {
  const re = new RegExp(BARE_URL_IN_TEXT.source, BARE_URL_IN_TEXT.flags);
  return text.replace(re, (raw) => {
    const norm = normalizeHttpUrl(raw);
    if (!norm) return raw;
    const label = norm.display.replace(/\\/g, '\\\\').replace(/\[/g, '\\[');
    return `[${label}](${norm.href})`;
  });
}

/** LLMs often wrap the whole reply in a ```markdown fence, which would render as a literal code block. */
export function stripOuterMarkdownFence(text: string): string {
  const trimmed = text.trim();
  const match = trimmed.match(/^```(?:markdown|md)?\s*\n([\s\S]*?)\n```\s*$/i);
  return match ? match[1] : text;
}

function leadingSpaces(line: string): number {
  const match = line.match(/^ */);
  return match ? match[0].length : 0;
}

/** If every non-empty line is indented 4+ spaces, CommonMark treats the block as code — dedent it. */
export function dedentIfFullyIndented(text: string): string {
  const lines = text.split('\n');
  const nonEmpty = lines.filter((line) => line.trim().length > 0);
  if (nonEmpty.length === 0) return text;

  const minIndent = Math.min(...nonEmpty.map(leadingSpaces));
  if (minIndent < 4) return text;

  return lines.map((line) => (line.length >= minIndent ? line.slice(minIndent) : line)).join('\n');
}

/** Replace raw workstream enum values (e.g. `ADK_Integration`) with display labels. */
export function formatWorkstreamNamesInText(text: string): string {
  return text.replace(WORKSTREAM_ENUM_PATTERN, (match) => formatWorkstream(match));
}

export function prepareAssistantMarkdown(text: string): string {
  const withoutFence = dedentIfFullyIndented(stripOuterMarkdownFence(text));
  const withWorkstreamLabels = formatWorkstreamNamesInText(withoutFence);
  const repairedLinks = repairAssistantLinkMarkdown(withWorkstreamLabels);
  const withoutAdjacentQuotes = stripAdjacentQuotesFromMarkdownLinks(repairedLinks);
  const withoutBold = stripBoldWrappingAroundMarkdown(withoutAdjacentQuotes);
  const { protectedText, links } = protectMarkdownLinks(withoutBold);
  const linkified = linkifyBareUrlsForMarkdown(protectedText);
  return repairAssistantLinkMarkdown(restoreMarkdownLinks(linkified, links));
}

const SEPARATE_SPA_PATH_PREFIXES = ['/docs', '/support'] as const;

function isSeparateSpaPath(pathname: string): boolean {
  return SEPARATE_SPA_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/** Same-origin paths suitable for React Router in the current SPA (excludes /docs and /support). */
export function toInternalRoute(href: string): string | null {
  let path: string | null = null;

  if (href.startsWith('/') && !href.startsWith('//')) {
    path = href;
  } else {
    try {
      const u = new URL(href);
      if (typeof window !== 'undefined' && u.origin === window.location.origin) {
        path = `${u.pathname}${u.search}${u.hash}`;
      }
    } catch {
      return null;
    }
  }

  if (!path) return null;

  const pathname = path.split(/[?#]/)[0];
  if (isSeparateSpaPath(pathname)) return null;

  return path;
}

/** Normalize href from markdown (trailing `**`, `)`, etc.) before navigation. */
export function resolveMarkdownHref(href: string): string {
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  const norm = normalizeHttpUrl(href);
  if (norm) return norm.href;
  try {
    return new URL(href).href;
  } catch {
    return href;
  }
}
